import React, { useState, useCallback } from 'react';
import { NavLink as RouterNavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Avatar, Dropdown, message } from 'antd';
import { UserOutlined, LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import NavbarSearch from './NavbarSearch';
import NavDropdown from './NavDropdown';
import './Navbar.css';

// Icon components (inline SVGs for consistency with mockup)
const DashboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/>
    <rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/>
  </svg>
);

const ContractsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const ShipmentsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
    <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
  </svg>
);

const ReportsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>
  </svg>
);

const FinancialIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23"/>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

const AtlasLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
  </svg>
);

const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, onClick }) => {
  return (
    <li className="nav-item">
      <RouterNavLink 
        to={to} 
        className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
        onClick={onClick}
      >
        {icon}
        {label}
      </RouterNavLink>
    </li>
  );
};

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      message.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      message.error('Failed to logout');
    }
  }, [logout, navigate]);

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => message.info('Profile page coming soon'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => message.info('Settings page coming soon'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Dropdown configurations
  const contractsDropdown = {
    icon: <ContractsIcon />,
    label: 'Contracts',
    items: [
      { to: '/contracts', label: 'List' },
      { to: '/contracts/create', label: 'Create New' },
    ],
  };

  const financialDropdown = {
    icon: <FinancialIcon />,
    label: 'Financial',
    items: [
      { to: '/financial', label: 'Overview' },
      { to: '/payments', label: 'Payments' },
      { to: '/commissions-due', label: 'Commissions Due' },
      { to: '/wire-entry', label: 'Wire Entry' },
      { to: '/check-entry', label: 'Check Entry' },
    ],
  };

  const settingsDropdown = {
    icon: <SettingsIcon />,
    label: 'Settings',
    items: [
      { to: '/contacts', label: 'Companies' },
      { to: '/forwarders', label: 'Forwarders' },
      { to: '/carriers', label: 'Carriers' },
      { to: '/document-requirements', label: 'Document Requirements' },
      { to: '/settings/templates', label: 'Document Templates' },
      { to: '/settings/snippets', label: 'Document Snippets' },
      { to: '/settings/company', label: 'Company Settings' },
      { to: '/settings/defaults', label: 'Financial Defaults' },
    ],
  };

  const reportsDropdown = {
    icon: <ReportsIcon />,
    label: 'Reports',
    items: [
      { to: '/reports/master-tracking', label: 'Master Tracking' },
      { to: '/reports/shipments-by-status', label: 'Shipments by Status' },
      { to: '/reports/outstanding-by-supplier', label: 'Outstanding by Supplier' },
      { to: '/reports/revenue', label: 'Revenue Report' },
      { to: '/tracking', label: 'Container Tracking' },
      { to: '/reports/shinyei-reconciliation', label: 'Shinyei Reconciliation' },
    ],
  };

  return (
    <nav className="navbar" role="navigation" aria-label="Main navigation">
      <div className="navbar-inner">
        {/* Brand */}
        <RouterNavLink to="/dashboard" className="navbar-brand">
          <AtlasLogo />
          <span>Atlas</span>
        </RouterNavLink>

        {/* Desktop Navigation */}
        <ul className="navbar-nav">
          <NavItem to="/dashboard" icon={<DashboardIcon />} label="Dashboard" />
          <NavDropdown {...contractsDropdown} />
          <NavItem to="/shipments" icon={<ShipmentsIcon />} label="Shipments" />
          <NavDropdown {...financialDropdown} />
          <NavDropdown {...reportsDropdown} />
          <NavDropdown {...settingsDropdown} />
        </ul>

        {/* Actions (Search + User) */}
        <div className="navbar-actions">
          <NavbarSearch />

          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <button
              className="user-menu-button"
              aria-label={`User menu for ${user?.firstName || 'User'}`}
              aria-haspopup="true"
            >
              <Avatar
                size={32}
                src={user?.avatar}
                icon={!user?.avatar && <UserOutlined />}
                style={{ backgroundColor: 'var(--navbar-brand-color)', cursor: 'pointer' }}
              >
                {!user?.avatar && user?.firstName ? user.firstName.charAt(0).toUpperCase() : null}
              </Avatar>
            </button>
          </Dropdown>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
        <ul className="mobile-nav-list">
          <NavItem to="/dashboard" icon={<DashboardIcon />} label="Dashboard" onClick={closeMobileMenu} />
          <NavDropdown {...contractsDropdown} onItemClick={closeMobileMenu} />
          <NavItem to="/shipments" icon={<ShipmentsIcon />} label="Shipments" onClick={closeMobileMenu} />
          <NavDropdown {...financialDropdown} onItemClick={closeMobileMenu} />
          <NavDropdown {...reportsDropdown} onItemClick={closeMobileMenu} />
          <NavDropdown {...settingsDropdown} onItemClick={closeMobileMenu} />
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
